import axios, { AxiosRequestConfig } from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqualWith';
import EventBus from './event';
import { getCookie } from 'steamer-browserutils';
import {
	ILiveDelegate,
    ClientReady,
    LiveStateChanged,
    MemberChanged,
    LiveWillBegin,
    LiveBegin,
    PageChanged,
    AllowFlipChanged,
    MeetingCodeChanged,
	MeetingInfoChanged,
	RoomNewInfoChanged,
    //Enum Define
    LiveRoleType,
    LiveState,
    AllowFlipState,
	ILiveInfo,
	IPenMoveEvent,
    IMediaSyncInfo,
    MediaStateChanged,
	FallbackToPolling,
	LiveSocketShutDown,
} from '../LiveConst';
import WebsocketClient from './websocket';
import { ILiveSocketMessageHandler, SendMsgResult } from './websocket/websocket-client.interface';
import { ShutDownErrorCode } from './websocket/define/error-code';
import { Command, IDisconnectReason } from '@tencent/docs-connection';
import type { IServerMessageBodyNoticeUserLeave, IServerMessageBodyNoticeUserNewInfo } from '@tencent/docs-connection';
import UserManager, { IUserInfo, IUserInfoServer } from './user-manager';
import { getRoomInfo, startPollingRoomInfo } from './polling-service';

const isTencentMeeting = getCookie('open_theme_id') === 'tencent_meeting';

export { IUserInfo, IUserInfoServer };

const isEqualInKeys = <T extends Record<string, any>, K extends keyof T>(obj: T, other: T, keys: K[]): boolean => {
	return keys.every(key => isEqual(obj[key], other[key]))
}

/**
 * 用于创建房间连接的参数
 */
export interface IServerVars{
	padId: any;
	globalPadId: any;
	pver: any;
	gray: any;
	appid?: any;
	wmode?: any;
	client?: IServerVarsClient;
	userId?: string;
}
export interface IServerVarsClient {
	upid: any;
	sid: any;
	tid: any;
	sig: any;
}

export interface IRquestUrlConfig{
	createShow: any;
	getShow: any;
	getAllShow: any;
}

/**
 * PPT直播manager
 */
export default class LiveCore implements ILiveDelegate, ILiveSocketMessageHandler {
	protected _roomInfo!: any;
	protected _role: LiveRoleType = LiveRoleType.None;
	protected _liveInfo!: ILiveInfo;
	protected _memberList: Array<IUserInfo>;
	protected _socketOpened!: boolean;
	protected _event!: EventBus;
	protected penQ!: Array<any>;
	protected penCmdQ!: Array<any>;
	protected timerPen!: number;
	protected receivePenQ!: Array<any>;
	protected receivePenCmdQ!: any;
	protected remoteBaseTS!: number;
	protected isNewlyReceived!: boolean;
	protected dawingBaseTS!: number;
	protected _isRemoteLaserOn!: boolean;
	protected hostname: string;
	/**
	 * 房间层启用全量 cookie
	 * @see WebsocketClient.useAllCookie 
	 */
	protected useAllCookieInSocket = false;

	protected moduleName: string = 'meetingRoom'; // 用于上报，品类继承后可赋值

    /**
     * socket通道
     */
	protected socketClient!: WebsocketClient;
	private userManager = new UserManager(this.getUserInfos.bind(this), () => this._event.emit(MemberChanged));
	private endPolling?: () => void;

	constructor() {
		this.init();
	}
	init() {
		this._socketOpened = false;
		this.penQ = [];
		this.penCmdQ = [];
		this.receivePenCmdQ = [];
		this._memberList = [];
		this._event = new EventBus();
		this.hostname = (window.location && window.location.hostname) || 'docs.qq.com';
	}
	/**
	 * 返回自己的直播角色
	 */
	public get role(): LiveRoleType {
		return this._role;
	}
	/**
	 * 是否是观众角色
	 */
	public get isAudience(): boolean {
		return this._role === LiveRoleType.Audience;
	}
	/**
	 * 返回演讲者的昵称
	 */
	public get masterName(): string {
		return this._liveInfo && this._liveInfo.name;
	}

	/** 
	 * @deprecated 返回演讲者的头像信息; 
	 * 
	 * FIXME: 
	 * 1. liveInfo 甚至没有演讲者 uid，当前只能用 name 来匹配头像
	 * 2. 应该收归为 liveInfo.masterUserInfo
	 */
	public get masterAvatar(): string | undefined {
		const masterInfo = this.memberList.find(({ name }) => name === this.masterName);
		return masterInfo?.userPic
	}
	/**
	 * 返回房间中的已登录成员列表
	 */
	public get memberList(): Array<IUserInfo> {
		return this.userManager.loginedUsers;
	}
	/**
	 * 返回房间中的人数
	 */
	public get memberCount(): number {
		return this.userManager.userCount;
	}
	/**
	 * 返回直播状态
	 */
	public get liveInfo(): any {
		return this._liveInfo;
	}
	/**
	 * 演示者的激光笔是否打开
	 */
	public get isRemoteLaserOn(): boolean {
		return this._isRemoteLaserOn;
	}
	/**
	 * 是否可以直播
	 * 子类可以覆盖
	 */
	public get enableLive(): boolean {
		let enable = true; // 远程演示的屏蔽开关
		return enable;
	}
	public get event(): EventBus {
		return this._event;
	}
	/**
	 * 设置直播状态：直播中、暂停、结束。会触发LiveStateChanged事件
	 * @param state LiveState枚举类型
	 */
	setLiveState(state: LiveState) {
		let s = state || LiveState.None;

		this._liveInfo.state = s;
		this.event.emit(LiveStateChanged, [this._liveInfo.state]);
	}
	allowFlip() {
		return this._liveInfo.allow_flip === AllowFlipState.YES;
	}
	/**
	 * 设置翻页权限
	 */
	setAllowFlip(allow: boolean) {
		console.log('live setAllowFlip：' + allow);
		this._liveInfo.allow_flip = allow ? AllowFlipState.YES : AllowFlipState.NO;
		this.sendLiveMessage('ALLOWFLIP');
	}
	protected setAllowFlipState(allow: AllowFlipState) {
		this._liveInfo.allow_flip = allow;
		this.event.emit(AllowFlipChanged, this._liveInfo.allow_flip);
	}

	getXSRF() {
		return getCookie('TOK');
	}

	/**
	 * 获取房间信息
	 * @param roomId 
	 * @returns 
	 */
	public async getRoomInfo(roomId = this._roomInfo.roomid) {
		if (!roomId) {
			return null;
		}
		return getRoomInfo(roomId);
	}
	
	/**
	 * 创建远程演示房间。
	 * @param id 文档ID
	 */
	createShowRequest(id: string) {
		let url = `${this.requestUrl.createShow}?id=${id}&xsrf=${this.getXSRF()}`;
		if (isTencentMeeting) {
			url += '&source=meeting';
		}
		let form = new FormData();
		form.append('id', id);
		let obj = { 'id': id};
		const userId = this.userId;
		const uid = UserManager.getRealUid(userId);
		let cfg: AxiosRequestConfig = { responseType: 'json', headers: { uid }, params: { info: { moduleName: this.moduleName } }};
		return axios.post(url, obj, cfg);
	}

	/**
	 * 创建房间
	 */
	createShow(callback: Function) {
		let docId = this.docID;
		this.createShowRequest(docId).then((response:any) => {
			if (response.data && response.data.hasOwnProperty('retcode') && 0 === response.data.retcode) {
				let room_id = response.data.result.roomid;
				console.log('create room id: ' + room_id);
				this._roomInfo = Object.assign({}, this._roomInfo, response.data.result);
				callback && callback(response.data.result);
				this.setLiveState(LiveState.Pause);
			}
			else {
				console.error('createShow failed:' + JSON.stringify(response));
				callback && callback({ error: response.data ?? { message: "response is empty" } });
			}
		}).catch(function (error) {
			console.log(error);
			callback && callback({ error: error });
		});

	}

	getShowRequest(id: string) {
		return axios.get(`${this.requestUrl.getShow}?id=${id}&xsrf=${this.getXSRF()}`);
	}

	/**
	* 查询/加入房间，文档内业务逻辑相关（查到room_id后直接加入即可）
	*/
	getShow(callback: Function) {
		if (!this.enableLive) {
			callback && callback({ error: 'enableLive = false' });
			return;
		}
		let docId = this.docID;
		this.getShowRequest(docId).then((response) => {
			if (response.data && response.data.hasOwnProperty('retcode') && 0 === response.data.retcode) {
				let room_id = response.data.result.roomid;
				console.log('get show room id: ' + room_id);
				this._roomInfo = Object.assign({}, this._roomInfo, response.data.result);
				callback && callback(response.data.result);
				this.masterEnter();
				this.setLiveState(LiveState.Pause);
			}
		}).catch(function (error) {
			console.log(error);
			callback && callback({ error: error });
		});
	}

	/**
	* 查询我的房间信息
	*/
	getMyRoomInfo(callback: Function) {
		if (!this.enableLive) {
			callback && callback({ error: 'enableLive = false' });
			return;
		}
		let docId = this.docID;
		this.getShowRequest(docId).then((response) => {
			if (response.data && response.data.hasOwnProperty('retcode') && 0 === response.data.retcode) {
				let room_id = response.data.result.roomid;
				console.log('get show room id: ' + room_id);
				this._roomInfo = Object.assign({}, this._roomInfo, response.data.result);
				callback && callback(response.data.result);
			}
		}).catch(function (error) {
			console.log(error);
			callback && callback({ error: error });
		});
	}

	getAllShowRequest(id: string) {
		return axios.get(`${this.requestUrl.getAllShow}?id=${id}&xsrf=${this.getXSRF()}`);
	}

	/**
	 * 查询当前文档的所有房间
	 */
	getAllShow() {
		return this.getAllShowRequest(this.docID);
	}
	/**
	 * 关闭房间
	 */
	closeShowRoom() {
		this._liveInfo.state = LiveState.Ended; //不用抛事件，因为关闭会被踢出房间，会触发事件
		this.sendLiveMessageWithCallback('STATE', (result:any) => {
			if (result.errCode === 0
				&& result.response
				&& result.response.onpost
				&& result.response.onpost.result === 0) { // 成功回包
				// 关闭后断开
				this.socketClient.close();
			} else { // 失败不做提示处理 nothing
			}
		});
		this.resetPenTimer();
	}
	/** 
	 * 开始远程播放
	*/
	liveWillBegin(): boolean {
		// TODO: 检查是否可以播放，不可以就返回fasle
		if (!this.enableLive) return false;
		this.event.emit(LiveWillBegin, null);

		return true;
	}
	liveBegin(eventArgs: any): boolean {
		this.setLiveState(LiveState.OnAir);
		this.sendLiveMessage('STATE');

		this.event.emit(LiveBegin, eventArgs);
		return true;
	}
	liveEnd() {
		this.setLiveState(LiveState.Pause);
		this.sendLiveMessage('STATE');
		this.resetLiveInfo();
		this.resetPenTimer();
	}
	
	/**
	* 将直播状态重置为初始值
	*/
	resetLiveInfo() {
		let conf = {
			allMute: 0,
			muteList: [],
		};
		this._liveInfo = {
			state: LiveState.Pause, // 直播状态
			name: '',
			points: '', // 激光笔
			page: 1,
			fragment: 1,
			allow_flip: AllowFlipState.YES,
			revision: 0,
			confid: '0',	// 语音房间号
			confmsg: JSON.stringify(conf)
		}
	}
	/**
	 * 重置激光笔发送计时器
	 */
	resetPenTimer() {
		if (this.timerPen !== null) {
			clearTimeout(this.timerPen);
		}
	}
	onLaserPenMoved(evt: IPenMoveEvent) {
		//子类实现
	}
	/**
	 * 发送激光笔数据
	 */
	sendLaserPoint(points: Array<any>) {
		console.log('live sendLaserPoint');
		if (points.length > 0) {
			this._liveInfo.points = JSON.stringify(points);
			this.sendLiveMessage('POINTS');
			this._liveInfo.points = '';
		}
	}
	/**
	 * 获取DocID，子类覆盖
	 */
	get docID(): string {
		return '';
	}

	// 演示链接URL, 子类覆盖
	get shareUrl(): string{
		return `https://${this.hostname}`;
	}

	sendLiveMessage(type: string) {
		this.sendLiveMessageWithCallback(type, () => {});
	}
	sendLiveMessageWithCallback(type: string, callback: (result: SendMsgResult) => void) {
		let docId = this.docID;
		let data = {
			type: type,
			docid: docId,
			room_info: this._liveInfo,
		}

		this.socketClient?.sendMessage(Command.LIVE_MESSAGE, JSON.stringify(data), callback);
	}

	/**
	 * 观看者是否已手动翻页（子类覆盖）
	 */
	get isManulFlipped(): boolean {
		return false;
	}

	/**
	 * 观看者获取演示者当前演讲页数信息（子类覆盖）
	 */
	get getAsyncInfo(): string {
		return '';
	}

	get requestUrl(): IRquestUrlConfig {
		return {
            createShow: `//${this.hostname}/show-api/createshow`,
            getShow: `//${this.hostname}/show-api/getshow`,
            getAllShow: `//${this.hostname}/show-api/getallshow`,
		};
	}

	/**
	 * @deprecated use `userId`
	 * 文档用户的TOID，子类实现
	 */
	get TOID(): string{
		const toid = (window as any)?.clientVars?.userInfo?.toid
		return toid ?? '';
	}

	/**
	 * 文档用户的 userId, 带前缀
	 */
	get userId(): string {
		console.error('userId not implements');
		return (window as any).clientVars?.userId ?? '';
	}

	/**
	* 用于创建房间连接的参数，一般是window.clientVars.collab_client_vars，子类实现
	*/
	get serverVars(): IServerVars {
		return {
			padId: 'any',
			globalPadId: 'any',
			pver: 'any',
			gray: 'any',
			appid: 'any',
			wmode: 'any',
			client: {
				upid: 'any',
				sid: 'any',
				tid: 'any',
				sig: 'any',
			},
		};
	}
	/**
	* 用于观众加入房间的参数，一般是window.clientVars，子类实现
	*/
	get audienceServerVars(): IServerVars {
		let vars = {
			padId: 'padId',
			globalPadId: 'globalPadId',
			pver: 'pver',
			gray: 'gray',
		}
		return vars
	}
	/**
	 * 演讲者加入房间
	 */
	masterEnter() {
		this._role = LiveRoleType.Master;
		this._roomInfo.isOwner = true;
		console.log('live master enter.');
		const userId = this.userId;
		const uid = UserManager.getRealUid(this.userId);
		this.userManager.setSelfUserId(userId);
		this.socketClient = new WebsocketClient(this, this.moduleName, uid, userId, this._roomInfo.roomid, this.serverVars, this.useAllCookieInSocket, true);
		this.socketClient.open();
	}
	/**
	 * 观众加入房间
	 */
	audienceEnter(roomid: string) {
		this._role = LiveRoleType.Audience;
		this._roomInfo = { roomid: roomid, isOwner: false };
		let info = this.audienceServerVars;
		let vars = {
			padId: info.padId,
			globalPadId: info.globalPadId,
			pver: info.pver,
			gray: info.gray,
		}
		let serverVars = this.serverVars || vars;
		console.log('live audience enter.');

		// 防前缀和兜底
		const userId = this.userId;
		const uid = UserManager.getRealUid(userId);
		this.userManager.setSelfUserId(userId);

		this.socketClient = new WebsocketClient(this, this.moduleName, uid, userId, roomid, serverVars, this.useAllCookieInSocket, false);
		this.socketClient.open();
	}

	/**
	 * 房间层满员，降级为 http 轮询 
	 * 只有观众可能触发，主讲人预期不会出现满员错误
	 */
	private fallbackToPolling() {
		const { roomid } = this._roomInfo;

		this.endPolling = startPollingRoomInfo(roomid, (info) => {
			const prevInfo = cloneDeep(this._liveInfo);
			this._liveInfo = { ...this._liveInfo, ...info };

			if (prevInfo.state !== info.state) this.setLiveState(info.state);

			if (!isEqualInKeys(prevInfo, info, ['page', 'fragment', 'iaFragmentInfo'])) {
				this.event.emit(PageChanged, this.targetPosition());
			}

			if (prevInfo.allow_flip !== info.allow_flip) {
				this.setAllowFlipState(info.allow_flip);
			}
		});
	}

	/**
	 * 实现interface 方法
	 * 返回房间信息，保存用于建立socket链接的字段
	 */
	roomInfo() {
		return this._roomInfo || { roomid: '', create_time: '', isOwner: false };
	}

	// 获得演讲人的演讲位置
	targetPosition() {
		let page = this._liveInfo.page;
		let frag = this._liveInfo.fragment;
		let target: {
			p: number;
			f: number;
			iaF?: string;
            stopA?: boolean;
            prev?: boolean;
		} = {
			p: page > 1 ? page - 1 : 0,
			f: frag >= 0 ? frag - 1 : 0,
		};
		// PPT 触发器动画参数
		if (this._liveInfo.iaFragmentInfo) {
			target.iaF = this._liveInfo.iaFragmentInfo;
		}
        if(this._liveInfo.shouldStopMainAnim) {
            target.stopA = this._liveInfo.shouldStopMainAnim;
        }
        if(typeof this._liveInfo.isLoadPageBySwitchForward === 'boolean') {
            target.prev = this._liveInfo.isLoadPageBySwitchForward;
        }
		return target;
	}

	handleMessageFromServer(type: string, data: any) {
		this.event.emit('receiveSocketMessage', type, data);
	}

	/** @deprecated use `getUserInfos` */
	getUserInfo(uid: string, callback = (info: IUserInfoServer) => {}) {}

	/** 子类实现, 批量获取用户信息接口 */	
	async getUserInfos(uids: string[]): Promise<IUserInfoServer[] | false> {
		console.error('getUserInfos not implements')
		const users = await Promise.all(
			uids.map(uid => new Promise<IUserInfoServer>(resolve => this.getUserInfo(uid, resolve)))
		);
		return users.filter(u => u.uid && (u.markName || u.userPic));
	}

	handleUserEnter(msg: IServerMessageBodyNoticeUserNewInfo) {
		this.userManager.handleUserEnter(msg);
	}

	handleUserLeave(msg: IServerMessageBodyNoticeUserLeave) {
		this.userManager.handleUserLeave(msg);
    }

	getNickname(): string {
		return '';
	}

	handleSocketOpen() {
		this.event.emit(ClientReady, null);
		this._socketOpened = true;

		// 进入房间之后设置昵称
		if (this._role === LiveRoleType.Master) {
			let nick = this.getNickname();
			if (nick) {
				this._liveInfo.name = nick;
				this.sendLiveMessage('NAME');
			}
		}
	}

	// 重连成功后发送STATE类型消息通知后台
	handleSocketReopen() {
		this.setLiveState(LiveState.OnAir);
		this.sendLiveMessage('STATE');
	}

	handleSocketShutDown(reason?: IDisconnectReason) {
		if (reason?.errCode === ShutDownErrorCode.RoomFull) {
			this.event.emit(FallbackToPolling);
			this.fallbackToPolling();
			return;
		}

		this._socketOpened = true;
		this.event.emit(LiveSocketShutDown, reason);
	}

	handleRoomNewInfo(info:any) {
		if (this._role === LiveRoleType.Audience) {
			console.log('live room info:',info);
			let state = info.state;
			delete info.state;
			//先更新房间的其他信息，再设置state。因为设置state后可能会直接出发播放了。
			this._liveInfo = Object.assign({}, this._liveInfo, info);
			this.setLiveState(state);
			this.event.emit(RoomNewInfoChanged);
		}
	}

	// 做演示状态兜底，对每一条消息，当消息里的state等于LiveState.OnAir且不等于当前state时，更新state
	handleLiveMessageForStateChange(nextState: LiveState) {
		const currentState = this._liveInfo.state;
		if (currentState !== nextState && nextState === LiveState.OnAir) this.setLiveState(nextState);
	}

	handleLiveMessage(data:any) {
		if (!this.needReceiveLiveMessage(data)) {
			return;
		}

		const nextState = data.room_info && data.room_info.state;
		this.handleLiveMessageForStateChange(nextState);

		switch (data.type) {
			case 'STATE':
				// 先赋值其他状态值，再触发stateChanged事件。
				let s = data.room_info.state;
				delete data.room_info.state;
				this._liveInfo = Object.assign({}, this._liveInfo, data.room_info);
				this.setLiveState(s);
				break;
			case 'REVISION':
				this._liveInfo = Object.assign({}, this._liveInfo, data.room_info);
				break;
			case 'PAGE':
			case 'FRAGMENT':
				let page = data.room_info && data.room_info.page || 0;
				let frag = data.room_info && data.room_info.fragment || 0;
				this._liveInfo.page = page;
				this._liveInfo.fragment = frag;
                this._liveInfo.shouldStopMainAnim = data.room_info.shouldStopMainAnim;
                this._liveInfo.isLoadPageBySwitchForward = data.room_info.isLoadPageBySwitchForward;
				this._liveInfo.shouldStopTransition = data.room_info.shouldStopTransition;
				// PPT 触发器动画参数
				let iaFrag = data.room_info && data.room_info.iaFragmentInfo;
				if (iaFrag) {
					this._liveInfo.iaFragmentInfo = iaFrag;
				}
				const extra = data.room_info && data.room_info.extra;
				if (extra){
					this._liveInfo.extra = extra;
				}
				this.event.emit(PageChanged, this.targetPosition());
				break;
			case 'ALLOWFLIP':
				let allow = data.room_info && data.room_info.allow_flip || 0;
				allow && this.setAllowFlipState(allow);
				break;
			case 'POINTS':
				let points = data.room_info && data.room_info.points || '';
				let pointArray = JSON.parse(points);
				if (Array.isArray(pointArray) && pointArray.length > 0) {
					console.log('LiveMessage GotPoints: ' + pointArray.length);
					// this.event.emit(GotPoinsts, pointArray);
					let firstPoint = pointArray[0];
					if (firstPoint && firstPoint.cmd) {
						// 命令都是单独一条消息
						this.handlePointsCmd(firstPoint);
					}
					else {
						while (pointArray.length > 0 && this.isBadPoint(firstPoint)) {
							pointArray.shift();
							firstPoint = pointArray[0];
						}
						if (!this.isBadPoint(firstPoint) && firstPoint.ts > 0) {
							this._isRemoteLaserOn = true;
							this.remoteBaseTS = firstPoint.ts;
							this.isNewlyReceived = true;
							this.receivePenQ = pointArray;
						}
						else {
							debugger;
						}
					}
				}
				break;
			case 'CONFID':
				let confid = data.room_info && data.room_info.confid || 0;
				if (confid) {
					this._liveInfo.confid = confid;
					this.event.emit(MeetingCodeChanged, this._liveInfo.confid);
				}
			// 收到confid时也走confmsg的逻辑
			case 'CONFMSG':
				let confmsg = data.room_info && data.room_info.confmsg;
				if (confmsg) {
					this._liveInfo.confmsg = confmsg;
					let obj = JSON.parse(confmsg);
					this.event.emit(MeetingInfoChanged, obj);
				}
				break;
			case 'MEDIA':
                this.handleMediaCmd(data.room_info.media);
			case 'BOARD':
				this._liveInfo = Object.assign({}, this._liveInfo, data.room_info);
				this.event.emit('BoardMessage', data.room_info);
				break;
			default:
				break;
		}
	}

    handleMediaCmd(mediaInfo?: IMediaSyncInfo) {
        if(!mediaInfo) return;
        this.event.emit(MediaStateChanged, mediaInfo);
    }
	/**
	 * 判断激光笔point的有效性
	 */
	isBadPoint = (point: any): boolean => {
		if (point && point.ts > 0) {
			return false;
		}
		return true;
	}
	/**
	 * 利用points字段可以广播自定义的命令，收到命令后在这里解析
	 */
	handlePointsCmd = (cmdObj: any) => {
		let cmd = cmdObj && cmdObj.cmd || 'NONE';
		switch (cmd) {
			case 'TURN_OFF':
				this._isRemoteLaserOn = false;
				break;
			default:
		}
	}
	/**
	 * 获取远程激光笔的下一个绘制点
	 */
	getRemotePoint = (): any => {
		if (this.isNewlyReceived) {
			this.isNewlyReceived = false;
			this.dawingBaseTS = new Date().getTime();
		}
		if (this.receivePenQ && this.receivePenQ.length > 0) {
			let p = this.receivePenQ.shift();
			let np = this.receivePenQ.length > 0 ? this.receivePenQ[0] : null;
			while (this.isNextBetter(p, np)) {
				console.log('live pen: DROP A POINT')
				p = this.receivePenQ.shift();
				np = this.receivePenQ.length > 0 ? this.receivePenQ[0] : null;
			}
			return p;

		}
		else {
			return null;
		}
	}
	protected isNextBetter = (first: any, next: any): boolean => {
		if (next) {
			let firstPeriod = Math.abs(first.ts - this.remoteBaseTS);
			let nextPeriod = Math.abs(next.ts - this.remoteBaseTS);
			let period = Math.abs(new Date().getTime() - this.dawingBaseTS);
			let firstDiff = Math.abs(firstPeriod - period);
			let nextDiff = Math.abs(nextPeriod - period);
			return nextDiff < firstDiff;
		}
		else {
			return false;
		}
	}

	/**
	 * 默认只有观众才需要接收live message消息
	 * 子类可重写
	 * @param data 
	 * @returns 
	 */
	protected needReceiveLiveMessage = (data) => {
		return this._role === LiveRoleType.Audience
	}
}

